import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, CheckCheck, Loader2, MessageCircleQuestion, Send } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  askCompanion, LEVEL_HINT, LEVEL_LABEL, SEVERITY_LABEL, timeAgo, useAssistantStatus, useMonitorStatus, useWatchList,
} from '@/lib/assistant';
import {
  disablePush, enablePush, notificationPermission, pushStatus, pushSupport,
} from '@/lib/push';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/Drawer';
import { SettingSwitch } from '@/components/ui/setting-switch';
import { LEVEL_ICON, SubscribeBell } from './SubscribeBell';
import { PushGuide } from './PushGuide';
import type { Device, Site } from '@/types';
import type { NotificationItem, NotificationList, NotificationPrefs, PushStatus, WatchLevel } from '@/types/assistant';
import './notifications.css';

export type CenterTab = 'inbox' | 'settings';
const PAGE = 30;

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-info', warning: 'bg-warn', critical: 'bg-destructive',
};

function SectionTitle({ children }: { children: string }) {
  return <h3 className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{children}</h3>;
}

export function NotificationCenter({ open, onOpenChange, tab, onTabChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tab: CenterTab;
  onTabChange: (tab: CenterTab) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} title="通知">
      <Tabs.Root value={tab} onValueChange={(v) => onTabChange(v as CenterTab)}>
        <Tabs.List className="tab-list -mt-1 mb-4" aria-label="通知中心">
          <Tabs.Trigger value="inbox" className="tab-trigger">通知</Tabs.Trigger>
          <Tabs.Trigger value="settings" className="tab-trigger">通知設定</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="inbox" className="focus-visible:outline-none">
          {open && <Inbox onNavigate={() => onOpenChange(false)} />}
        </Tabs.Content>
        <Tabs.Content value="settings" className="focus-visible:outline-none">
          {open && <Settings />}
        </Tabs.Content>
      </Tabs.Root>
    </Drawer>
  );
}

/* ── 通知清單 ───────────────────────────────────────── */

function Inbox({ onNavigate }: { onNavigate: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canAsk = useAssistantStatus().data?.ready === true;
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ['notifications', 'list', user?.username, unreadOnly],
    queryFn: ({ pageParam }) => apiGet<NotificationList>(
      `/api/v1/notifications?limit=${PAGE}${pageParam ? `&before_id=${pageParam}` : ''}${unreadOnly ? '&unread_only=true' : ''}`),
    initialPageParam: 0,
    getNextPageParam: (last) => (last?.items?.length === PAGE ? last.items[PAGE - 1].id : undefined),
  });
  const read = useMutation({
    mutationFn: (body: { ids?: number[]; all?: boolean }) => apiPost('/api/v1/notifications/read', body),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = list.data?.pages.flatMap((p) => p?.items ?? []) ?? [];
  const unread = list.data?.pages[0]?.unread ?? 0;

  function openItem(item: NotificationItem) {
    if (!item.read_at) read.mutate({ ids: [item.id] });
    if (item.link) {
      onNavigate();
      navigate(item.link);
    }
  }

  /* 先關掉通知抽屜，等焦點歸位後再打開小哨，兩個對話框才不會搶焦點 */
  function explain(item: NotificationItem) {
    if (!item.read_at) read.mutate({ ids: [item.id] });
    onNavigate();
    // 連結可能帶篩選（/devices?status=offline），情境只取頁面；說明的重點是異常本身
    const path = item.link?.startsWith('/') ? item.link.split(/[?#]/)[0] : undefined;
    window.setTimeout(() => askCompanion({
      text: `請說明這則通知：「${item.title}」。可能是什麼原因？建議怎麼處理？`,
      context: { path, device_id: item.device_key ?? undefined, incident_id: item.incident_id ?? undefined },
      fresh: true,
    }), 0);
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-muted-foreground" role="status">
          {unread > 0 ? `${unread.toLocaleString('zh-TW')} 則未讀` : '都看過了'}
        </p>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant={unreadOnly ? 'default' : 'secondary'} aria-pressed={unreadOnly}
            onClick={() => setUnreadOnly((v) => !v)}>只看未讀</Button>
          <Button size="sm" variant="ghost" disabled={unread === 0 || read.isPending}
            onClick={() => read.mutate({ all: true })}>
            <CheckCheck className="h-4 w-4" />全部標為已讀
          </Button>
        </div>
      </div>

      {list.isPending ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : list.isError ? (
        <p className="py-10 text-center text-[13px] text-destructive">
          通知載入失敗：{list.error instanceof Error ? list.error.message : '未知錯誤'}
        </p>
      ) : items.length === 0 ? (
        <div className="py-12 text-center">
          <BellRing className="pop mx-auto h-8 w-8 text-muted-foreground/60" />
          <p className="mt-3 text-[13.5px] font-semibold">{unreadOnly ? '沒有未讀的通知' : '目前沒有通知'}</p>
          <p className="mx-auto mt-1 max-w-[320px] text-[12.5px] leading-relaxed text-muted-foreground">
            裝置離線、SD 卡空間不足、上傳失敗或韌體更新有結果時，小哨會通知你。
          </p>
        </div>
      ) : (
        <ul className="stagger -mx-2 space-y-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" className="notice-item" data-unread={!item.read_at}
                onClick={() => openItem(item)}>
                <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-sm ${SEVERITY_DOT[item.severity] ?? 'bg-info'}`} />
                <span className="min-w-0 flex-1">
                  <span className={`block text-[13.5px] leading-snug ${item.read_at ? 'font-medium' : 'font-semibold'}`}>
                    <span className="sr-only">{SEVERITY_LABEL[item.severity]}：</span>{item.title}
                  </span>
                  {item.body && <span className="mt-0.5 block whitespace-pre-line text-[12.5px] leading-relaxed text-muted-foreground">{item.body}</span>}
                  <time dateTime={item.created_at} title={new Date(item.created_at).toLocaleString('zh-TW', { hour12: false })}
                    className="mt-1 block text-[11.5px] text-muted-foreground">
                    {timeAgo(item.created_at)}{item.read_at ? '' : '・未讀'}
                  </time>
                </span>
              </button>
              {canAsk && item.incident_id && <div className="notice-actions">
                <Button size="sm" variant="secondary" onClick={() => explain(item)}>
                  <MessageCircleQuestion className="h-4 w-4" />請小哨說明
                </Button>
              </div>}
            </li>
          ))}
        </ul>
      )}

      {list.hasNextPage && (
        <div className="mt-3 text-center">
          <Button size="sm" variant="secondary" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? '載入中…' : '載入更早的通知'}
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── 通知設定 ───────────────────────────────────────── */

function Settings() {
  return (
    <div className="space-y-7">
      <PushSection />
      <WatchSection />
      <PrefsSection />
      <MonitorLine />
    </div>
  );
}

const SUPPORT_HINT = {
  insecure: '背景推播需要用 https 開啟網站（本機 127.0.0.1 例外）。目前的連線無法開啟。',
  'needs-install': 'iPhone、iPad 要先加入主畫面，並從主畫面開啟平台，才能開啟背景推播。步驟請看下方教學。',
  unsupported: '這個瀏覽器不支援背景推播，站內通知照常運作。可以照下方教學改用支援的瀏覽器。',
} as const;

function PushSection() {
  const { user } = useAuth();
  const account = user?.username ?? '';
  const support = pushSupport();
  const [state, setState] = useState<{ subscribed: boolean; status: PushStatus | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function refresh() {
    try { setState(await pushStatus()); }
    catch { setState({ subscribed: false, status: null }); }
  }
  useEffect(() => { if (support === 'supported') void refresh(); }, [support]);

  const enabled = Boolean(state?.subscribed && state.status?.registered);
  const denied = notificationPermission() === 'denied';
  const reason = state?.status?.disabled_reason;

  async function run(task: () => Promise<unknown>, done: string) {
    setBusy(true); setError(''); setMessage('');
    try {
      await task();
      setMessage(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失敗');
    } finally {
      setBusy(false);
      await refresh();
    }
  }

  async function sendTest() {
    const r = await apiPost<{ ok: boolean; push_queued: number }>('/api/v1/notifications/test');
    return r?.push_queued ?? 0;
  }

  return (
    <section>
      <SectionTitle>這台裝置的背景推播</SectionTitle>
      <p className="text-[12.5px] leading-relaxed text-muted-foreground">
        開啟後，就算關掉分頁，系統也會跳出小哨的通知。每台電腦或手機要各自開啟；推播可能因網路或系統省電設定延遲，完整紀錄以通知清單為準。
      </p>

      {support !== 'supported' ? (
        <p className="callout mt-3 rounded-md bg-secondary px-3.5 py-3 text-[12.5px] leading-relaxed">{SUPPORT_HINT[support]}</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="mr-auto inline-flex items-center gap-2 text-[13px] font-semibold" role="status">
              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${enabled ? 'bg-success' : 'bg-muted-foreground'}`} />
              {state === null ? '確認中…' : enabled ? '已開啟' : '未開啟'}
            </span>
            {enabled ? (
              <>
                <Button size="sm" variant="secondary" disabled={busy}
                  onClick={() => void run(async () => {
                    const queued = await sendTest();
                    if (!queued) throw new Error('測試通知已放進通知清單，但這個帳號目前沒有可用的推播端點');
                  }, '已送出測試通知。現在鎖定螢幕或切到其他 App，約 5 秒內應該會跳出通知；沒有出現的話，請照下方教學檢查。')}>
                  <Send className="h-4 w-4" />送測試通知
                </Button>
                <Button size="sm" variant="ghost" disabled={busy}
                  onClick={() => void run(() => disablePush(account), '已關閉這台裝置的推播。')}>關閉</Button>
              </>
            ) : (
              <Button size="sm" disabled={busy || denied}
                onClick={() => void run(() => enablePush(account), '已開啟。可以按「送測試通知」確認。')}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
                開啟背景推播
              </Button>
            )}
          </div>
          {denied && (
            <p className="callout mt-2 text-[12.5px] leading-relaxed text-destructive">
              瀏覽器已封鎖這個網站的通知。請點網址列左側的網站設定，把「通知」改成允許，再重新整理。
            </p>
          )}
          {!enabled && reason && (
            <p className="callout mt-2 text-[12.5px] leading-relaxed text-warn-on">上次停用的原因：{reason}</p>
          )}
          {enabled && state?.status?.last_success_at && (
            <p className="mt-2 text-[12px] text-muted-foreground">上次送達推播服務：{timeAgo(state.status.last_success_at)}</p>
          )}
        </>
      )}
      {message && <p className="callout mt-2 text-[12.5px] text-success-on" role="status">{message}</p>}
      {error && <p className="callout mt-2 text-[12.5px] text-destructive" role="alert">{error}</p>}
      {/* 等推播狀態確認完才掛上，預設展開或收合才會對；https 都沒有時教學也幫不上忙 */}
      {support !== 'insecure' && (support !== 'supported' || state !== null) && <PushGuide defaultOpen={!enabled} />}
    </section>
  );
}

function WatchSection() {
  const qc = useQueryClient();
  const { data: watch } = useWatchList();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => apiGet<Site[]>('/api/v1/sites') });
  const devices = useQuery({ queryKey: ['devices'], queryFn: () => apiGet<Device[]>('/api/v1/devices') });
  const [error, setError] = useState('');
  const overall = useMutation({
    mutationFn: (level: WatchLevel) => apiPut('/api/v1/notifications/subscriptions', { scope_type: 'all', scope_key: '', level }),
    onMutate: () => setError(''),
    onError: (e) => setError(e instanceof Error ? e.message : '設定失敗'),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notifications', 'subscriptions'] }),
  });
  const current = overall.isPending && overall.variables ? overall.variables : watch?.overall ?? 'important';
  const deviceRows = (watch?.items ?? []).filter((s) => s.scope_type === 'device');
  const siteOf = (key: string) => (Array.isArray(devices.data) ? devices.data : []).find((d) => d.device_id === key)?.site_code;
  const siteList = Array.isArray(sites.data) ? sites.data : [];

  return (
    <section>
      <SectionTitle>要通知哪些事</SectionTitle>
      <div role="radiogroup" aria-label="整體通知層級" className="segmented">
        {(['all', 'important', 'off'] as WatchLevel[]).map((level) => {
          const Icon = LEVEL_ICON[level];
          return (
            <button key={level} type="button" role="radio" aria-checked={current === level}
              disabled={overall.isPending} onClick={() => current !== level && overall.mutate(level)}>
              <Icon className="h-4 w-4" />{LEVEL_LABEL[level]}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
        {LEVEL_HINT[current]}。這是所有裝置的預設，場域或單台裝置可以另外設定，設定越細的越優先。
      </p>
      {error && <p className="mt-1 text-[12.5px] text-destructive" role="alert">{error}</p>}

      {siteList.length > 0 && (
        <div className="mt-4">
          <h4 className="text-[12.5px] font-semibold">依場域</h4>
          <ul className="mt-1 divide-y divide-border">
            {siteList.map((s) => (
              <li key={s.code} className="flex min-h-[52px] items-center justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate text-[13px]">
                  <span className="font-mono text-[11.5px] text-muted-foreground">{s.code}</span>　{s.name}
                </span>
                <SubscribeBell scope="site" scopeKey={s.code} name={s.name} showLabel />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4">
        <h4 className="text-[12.5px] font-semibold">個別裝置</h4>
        {deviceRows.length === 0 ? (
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
            還沒有個別設定。到「裝置」頁，按裝置名稱旁的鈴鐺就能單獨調整。
          </p>
        ) : (
          <ul className="mt-1 divide-y divide-border">
            {deviceRows.map((s) => (
              <li key={s.scope_key} className="flex min-h-[52px] items-center justify-between gap-3 py-1.5">
                <span className="min-w-0">
                  <span className="block truncate text-[13px]">{s.label ?? s.scope_key}</span>
                  {s.label && s.label !== s.scope_key && (
                    <span className="block truncate font-mono text-[11px] text-muted-foreground">{s.scope_key}</span>
                  )}
                </span>
                <SubscribeBell scope="device" scopeKey={s.scope_key} name={s.label ?? s.scope_key}
                  siteCode={siteOf(s.scope_key)} showLabel />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const SELECT = 'min-h-[44px] rounded-sm border border-border bg-card px-3 text-sm disabled:opacity-50';

function PrefsSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ['notifications', 'prefs', user?.username];
  const { data } = useQuery({ queryKey: key, queryFn: () => apiGet<NotificationPrefs>('/api/v1/notifications/prefs') });
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (next: NotificationPrefs) => apiPut<NotificationPrefs>('/api/v1/notifications/prefs', next),
    onMutate: (next) => { setError(''); qc.setQueryData(key, next); },
    onError: (e) => setError(e instanceof Error ? e.message : '儲存失敗'),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  const prefs: NotificationPrefs = {
    push_detail: data?.push_detail ?? true,
    quiet_start: data?.quiet_start ?? null,
    quiet_end: data?.quiet_end ?? null,
    daily_digest: data?.daily_digest ?? true,
  };
  const quiet = prefs.quiet_start !== null && prefs.quiet_end !== null;
  const hour = (h: number) => `${String(h).padStart(2, '0')}:00`;

  return (
    <section>
      <SectionTitle>推播的顯示方式</SectionTitle>
      <SettingSwitch label="安靜時段" checked={quiet}
        description="這段時間只推播「嚴重」的通知，其他的照樣留在通知清單。時間以台灣時間計算。"
        onChange={(on) => save.mutate({ ...prefs, quiet_start: on ? 22 : null, quiet_end: on ? 7 : null })} />
      {quiet && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-[13px]">
          <label className="sr-only" htmlFor="quiet-start">安靜時段開始</label>
          <select id="quiet-start" className={SELECT} value={prefs.quiet_start ?? 22} disabled={save.isPending}
            onChange={(e) => save.mutate({ ...prefs, quiet_start: Number(e.target.value) })}>
            {HOURS.map((h) => <option key={h} value={h}>{hour(h)}</option>)}
          </select>
          <span className="text-muted-foreground">到</span>
          <label className="sr-only" htmlFor="quiet-end">安靜時段結束</label>
          <select id="quiet-end" className={SELECT} value={prefs.quiet_end ?? 7} disabled={save.isPending}
            onChange={(e) => save.mutate({ ...prefs, quiet_end: Number(e.target.value) })}>
            {HOURS.map((h) => <option key={h} value={h}>{hour(h)}</option>)}
          </select>
        </div>
      )}
      <SettingSwitch label="鎖定畫面顯示通知內容" checked={prefs.push_detail}
        description="關閉後，推播只會寫「有新的系統通知」，裝置名稱與細節要登入後才看得到。"
        onChange={(on) => save.mutate({ ...prefs, push_detail: on })} />
      <SettingSwitch label="每日值班摘要" checked={prefs.daily_digest}
        description="每天在管理員設定的時間（預設早上 8 點），小哨整理過去 24 小時的裝置、異常、事件與上傳狀況，放進通知清單；開了背景推播的裝置也會收到。"
        onChange={(on) => save.mutate({ ...prefs, daily_digest: on })} />
      {error && <p className="text-[12.5px] text-destructive" role="alert">{error}</p>}
    </section>
  );
}

function MonitorLine() {
  const { data } = useMonitorStatus();
  if (!data || typeof data.alive !== 'boolean') return null;
  return (
    <section className="border-t border-border pt-4 text-[12px] leading-relaxed text-muted-foreground">
      <p className="inline-flex items-center gap-2">
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${data.alive ? 'bg-success' : 'bg-warn'}`} />
        {data.alive
          ? `監控服務運作中，${timeAgo(data.last_run_at)}檢查過一次。`
          : '監控服務暫時沒有回應，異常通知可能會延遲。'}
      </p>
      {data.last_error && <p className="mt-1 break-words">最近一次錯誤：{data.last_error}</p>}
    </section>
  );
}
